// Pure aggregation functions for rolling per-user completion stats up into
// agency-wide totals. Extracted verbatim from useAgencyDashboard.ts's useMemo
// bodies so the exact same logic can be reused outside a React hook — the
// compliance portal needs to compute this for an arbitrary agency rather than
// the logged-in manager's own. This is a lift, not a rewrite: behavior must
// stay byte-for-byte identical to what was inline in useAgencyDashboard.ts.

export interface AgencyUserStats {
  user: { id: string; displayName: string; email?: string; [key: string]: any };
  documents: any[];
  fieldValues: { [key: string]: any };
  stats: {
    overall: {
      totalItems: number;
      completedItems: number;
      missingItems: number;
      expiredItems: number;
      completionRate: number;
    };
    categories: Record<string, {
      totalItems: number;
      completedItems: number;
      missingItems: number;
      expiredItems: number;
      completionRate: number;
    }>;
  };
}

export const aggregateAgencyStats = (
  agencyUsers: AgencyUserStats[],
  agentDocumentTypesCount: number,
  agentDocumentFieldsCount: number
) => {
  let statsObj = {
    totalItems: agentDocumentTypesCount + agentDocumentFieldsCount,
    completedItems: 0,
    missingItems: 0,
    expiredItems: 0,
    completionRate: 0
  };

  agencyUsers.forEach(agent => {
    statsObj.completedItems += agent.stats.overall.completedItems;
    statsObj.expiredItems += agent.stats.overall.expiredItems;
    statsObj.missingItems += agent.stats.overall.missingItems;
    statsObj.totalItems += agent.stats.overall.totalItems;
  });

  statsObj.completionRate = statsObj.totalItems > 0
    ? statsObj.completedItems / statsObj.totalItems
    : 0;

  return statsObj;
};

export const aggregateCategoriesWithStats = (categories: any[], agencyUsers: AgencyUserStats[]) => {
  // Build a stats accumulator map so we never mutate the mapped array
  const statsMap: Record<string, {
    completedItems: number;
    totalItems: number;
    expiredItems: number;
    missingItems: number;
    completionRate: number;
  }> = {};

  categories.forEach((cat: any) => {
    statsMap[cat.id] = {
      completedItems: 0,
      totalItems: 0,
      expiredItems: 0,
      missingItems: 0,
      completionRate: 0,
    };
  });

  agencyUsers.forEach(user => {
    Object.entries(user.stats.categories).forEach(([catKey, cat]: [string, any]) => {
      if (!statsMap[catKey]) return; // guard against unknown category keys
      statsMap[catKey].totalItems += cat.totalItems;
      statsMap[catKey].completedItems += cat.completedItems;
      statsMap[catKey].missingItems += cat.missingItems;
      statsMap[catKey].expiredItems += cat.expiredItems;
    });
  });

  return categories.map((cat: any) => {
    const stats = statsMap[cat.id];
    return {
      ...cat,
      stats: {
        ...stats,
        completionRate: (stats.completedItems / stats.totalItems * 100) || 0,
      },
    };
  });
};

export const calculateCompanyStats = (
  companyDocuments: any[] | null,
  companyFieldValues: Record<string, any> | null,
  companyDocumentCount: number,
  companyDocumentFieldCount: number,
  now: Date = new Date()
) => {
  const docs = Array.isArray(companyDocuments) ? companyDocuments : [];
  const validDocs = docs.filter((doc: any) => {
    if (!doc) return false;
    const expiry = doc.expiryDate ? new Date(doc.expiryDate) : null;
    const validPeriod = (doc.period_start && doc.period_end) ? new Date(doc.period_start) < new Date() && new Date() < new Date(doc.period_end) : true;
    return (!expiry || expiry > now) && validPeriod;
  });
  const completedDocs = validDocs.length;
  const totalItems = (Number(companyDocumentCount) || 0) + (Number(companyDocumentFieldCount) || 0);
  const fieldValuesCount = companyFieldValues ? Object.keys(companyFieldValues).length : 0;
  const completedItems = completedDocs + fieldValuesCount;
  const completionRate = totalItems > 0 ? (completedItems / totalItems) * 100 : 0;

  return {
    completedItems,
    totalItems,
    completionRate
  };
};

export const mapRiskAgents = (agencyUsers: AgencyUserStats[]) => {
  return agencyUsers.map(user => ({
    id: user.user.id,
    name: user.user.displayName,
    rate: user.stats.overall.completionRate
  }));
};

export const mapAgencyChartData = (agencyUsers: AgencyUserStats[]) => {
  return agencyUsers.map(user => ({
    name: user.user.displayName,
    completionRate: user.stats.overall.completionRate,
    completedItems: user.stats.overall.completedItems,
    totalItems: user.stats.overall.totalItems
  }));
};
